// Importaciones de React y sus hooks.
import React, { useState, useEffect } from 'react';

/**
 * Componente Calendario.
 * Muestra un calendario de eventos dinámico. Obtiene los datos de un webhook,
 * renderiza un calendario mensual, resalta los días con eventos, muestra el próximo
 * evento y permite ver los detalles de cada evento en una ventana modal.
 */
const Calendario = () => {
  // --- ESTADOS DEL COMPONENTE ---
  const [currentDate, setCurrentDate] = useState(new Date()); // La fecha actual para saber qué mes mostrar.
  const [eventos, setEventos] = useState([]); // Array para almacenar todos los eventos obtenidos del webhook.
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null); // El evento que se muestra en el modal.
  const [mostrarModal, setMostrarModal] = useState(false); // Controla la visibilidad del modal.
  const [proximoEvento, setProximoEvento] = useState(null); // Almacena el evento más cercano en el futuro.

  // --- EFECTO PARA CARGAR LOS DATOS DE LOS EVENTOS ---
  useEffect(() => {
    // Función asíncrona para obtener los eventos desde el webhook.
    const cargarEventos = async () => {
      try {
        // Realiza la petición POST al endpoint del webhook.
        const response = await fetch('https://n8n.srv871810.hstgr.cloud/webhook/5b465c33-07b0-44de-a4a6-389e18456536', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mensaje: 'calendario' }) // Cuerpo del mensaje esperado por el webhook.
        });
        
        if (!response.ok) {
          throw new Error('Error en la petición al webhook');
        }

        const data = await response.json();
        
        // Mapea los datos recibidos para formatearlos adecuadamente.
        const eventosWebhook = data.map(evento => ({
          display_name: evento.display_name,
          date_begin: new Date(evento.date_begin).toISOString(),
          date_end: new Date(evento.date_end).toISOString(),
          description: evento.description,
          location: evento.address_inline,
          organizer: evento.organizer_id[1]
        }));
        
        // Ordena los eventos por fecha de inicio, del más antiguo al más reciente.
        const eventosOrdenados = eventosWebhook.sort((a, b) => new Date(a.date_begin) - new Date(b.date_begin));
        
        // Encuentra el primer evento que ocurra después de la fecha y hora actual.
        const ahora = new Date();
        const proximo = eventosOrdenados.find(evento => new Date(evento.date_begin) > ahora);
        
        // Actualiza los estados con los datos procesados.
        setEventos(eventosOrdenados);
        setProximoEvento(proximo);
        
      } catch (error) {
        console.error('Error al cargar eventos:', error);
      }
    };

    cargarEventos();
  }, []); // El array vacío `[]` asegura que este efecto se ejecute solo una vez al montar el componente.

  // --- MANEJO DEL MODAL ---

  // Abre el modal y establece el evento seleccionado.
  const handleEventoClick = (evento) => {
    setEventoSeleccionado(evento);
    setMostrarModal(true);
  };

  // Cierra el modal y limpia el evento seleccionado.
  const cerrarModal = () => {
    setMostrarModal(false);
    setEventoSeleccionado(null);
  };

  // --- LÓGICA DE RENDERIZADO DEL CALENDARIO ---

  // Devuelve el número de días en el mes actual.
  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  // Devuelve el día de la semana en que comienza el mes (0=Domingo, 1=Lunes, ...).
  const firstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Función principal que construye la cuadrícula de días del mes.
  const renderDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);

    // Añade celdas vacías para los días antes del inicio del mes.
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="day empty"></div>);
    }

    // Itera y renderiza cada día del mes.
    for (let i = 1; i <= totalDays; i++) {
      const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      
      // Filtra los eventos que ocurren en el día actual.
      const eventosDelDia = eventos.filter(evento => {
        const inicio = new Date(new Date(evento.date_begin).setHours(0,0,0,0));
        const fin = new Date(new Date(evento.date_end).setHours(0,0,0,0));
        return currentDay >= inicio && currentDay <= fin;
      });

      days.push(
        <div 
          key={i} 
          className={`day ${eventosDelDia.length > 0 ? 'event-day' : ''}`}
        >
          {i}
          {eventosDelDia.map((evento, index) => (
            <div 
              key={index} 
              className="event-details"
              onClick={() => handleEventoClick(evento)}
            >
              <div className="event-title">{evento.display_name}</div>
            </div>
          ))}
        </div>
      );
    }

    return days;
  };

  // --- NAVEGACIÓN DEL CALENDARIO ---

  // Cambia al mes anterior.
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Cambia al mes siguiente.
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  return (
    <div className="p-2 md:p-4 bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
      {/* Contenedor principal que usa Grid para alinear el calendario y la tarjeta de próximo evento. */}
      <div className="calendar-container">
        
        {/* Sección del Calendario */}
        <div className="calendar">
          <div className="calendar-header">
            <button onClick={prevMonth} className="calendar-nav animate-scale text-sm md:text-base">&lt;</button>
            <h3 className="calendar-title animate-slide-in text-base md:text-2xl">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button onClick={nextMonth} className="calendar-nav animate-scale text-sm md:text-base">&gt;</button>
          </div>
          <div className="calendar-grid">
            {dayNames.map(day => (
              <div key={day} className="day-name animate-fade-in text-xs md:text-sm">{day}</div>
            ))}
            {renderDays()}
          </div>
        </div>

        {/* Sección de "Próximo Evento" */}
        <div className="proximo-evento bg-gradient-to-r from-blue-900 to-blue-800 p-2 md:p-4">
          <h3 className="proximo-evento-titulo text-sm md:text-xl">Próximo Evento</h3>
          {proximoEvento ? (
            <div className="proximo-evento-card p-2 md:p-4">
              <div className="proximo-evento-fecha p-2 md:p-3">
                <span className="proximo-evento-dia text-xl md:text-4xl">{new Date(proximoEvento.date_begin).getDate()}</span>
                <span className="proximo-evento-mes text-xs md:text-lg">{monthNames[new Date(proximoEvento.date_begin).getMonth()]}</span>
              </div>
              <div className="proximo-evento-info">
                <h4 className="proximo-evento-nombre text-xs md:text-xl">{proximoEvento.display_name}</h4>
                <p className="proximo-evento-hora text-xs md:text-base">
                  {new Date(proximoEvento.date_begin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                  {new Date(proximoEvento.date_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
                <p className="proximo-evento-ubicacion text-xs md:text-sm">📍 {proximoEvento.location}</p>
                <button 
                  className="proximo-evento-boton text-xs md:text-sm py-1 md:py-2 px-2 md:px-4"
                  onClick={() => handleEventoClick(proximoEvento)}
                >
                  Ver Detalles
                </button>
              </div>
            </div>
          ) : (
            <div className="proximo-evento-vacio text-xs md:text-base">
              <p>No hay eventos programados próximamente</p>
            </div>
          )}
        </div>
      </div>

      {/* Ventana Modal (se muestra condicionalmente) */}
      {mostrarModal && eventoSeleccionado && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content p-2 md:p-6" onClick={e => e.stopPropagation()}>
            <div className="modal-header p-2 md:p-4">
              <h3 className="modal-title text-sm md:text-xl">{eventoSeleccionado.display_name}</h3>
              <button className="modal-close text-lg md:text-2xl" onClick={cerrarModal}>×</button>
            </div>
            <div className="modal-body p-2 md:p-4">
              <div className="modal-info-grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                <div className="modal-info-item p-2 md:p-3">
                  <span className="modal-info-icon text-xs md:text-base">📅</span>
                  <div>
                    <p className="modal-info-label text-xs md:text-sm">Fecha de inicio</p>
                    <p className="modal-info-value text-xs md:text-base">{new Date(eventoSeleccionado.date_begin).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="modal-info-item p-2 md:p-3">
                  <span className="modal-info-icon text-xs md:text-base">📅</span>
                  <div>
                    <p className="modal-info-label text-xs md:text-sm">Fecha de fin</p>
                    <p className="modal-info-value text-xs md:text-base">{new Date(eventoSeleccionado.date_end).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="modal-info-item p-2 md:p-3">
                  <span className="modal-info-icon text-xs md:text-base">📍</span>
                  <div>
                    <p className="modal-info-label text-xs md:text-sm">Ubicación</p>
                    <p className="modal-info-value text-xs md:text-base">{eventoSeleccionado.location}</p>
                  </div>
                </div>
                <div className="modal-info-item p-2 md:p-3">
                  <span className="modal-info-icon text-xs md:text-base">👤</span>
                  <div>
                    <p className="modal-info-label text-xs md:text-sm">Organizador</p>
                    <p className="modal-info-value text-xs md:text-base">{eventoSeleccionado.organizer}</p>
                  </div>
                </div>
              </div>
              <div className="modal-description p-2 md:p-4">
                <h4 className="modal-description-title text-xs md:text-lg">Descripción</h4>
                {/* `dangerouslySetInnerHTML` se usa para renderizar HTML que viene del webhook. */}
                <div className="modal-description-content text-xs md:text-base" dangerouslySetInnerHTML={{ __html: eventoSeleccionado.description }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estilos CSS y animaciones inyectados directamente en el componente. */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideIn {
            from { transform: translateY(-20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          
          @keyframes scale {
            from { transform: scale(1); }
            to { transform: scale(1.1); }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          
          @keyframes shine {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes glow {
            0% { box-shadow: 0 0 5px rgba(6, 28, 91, 0.1); }
            50% { box-shadow: 0 0 20px rgba(6, 28, 91, 0.2); }
            100% { box-shadow: 0 0 5px rgba(6, 28, 91, 0.1); }
          }
          
          .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
          }
          
          .animate-slide-in {
            animation: slideIn 0.5s ease-out forwards;
          }
          
          .animate-scale {
            animation: scale 0.2s ease-out forwards;
          }
          
          .calendar {
            border: 1px solid #e2e8f0;
            border-radius: 0.75rem;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            background-color: white;
            animation: fadeIn 0.8s ease-out forwards;
          }
          
          .calendar-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem;
            background: linear-gradient(135deg, #061C5B 0%, #0A2B7C 100%);
            background-size: 200% 200%;
            animation: shine 8s ease infinite;
            color: white;
          }
          
          .calendar-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin: 0;
          }
          
          .calendar-nav {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: white;
            padding: 0.5rem 1rem;
            transition: all 0.3s;
          }
          
          .calendar-nav:hover {
            background-color: rgba(255, 255, 255, 0.1);
            border-radius: 0.5rem;
            transform: scale(1.1);
          }
          
          .calendar-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 1px;
            background-color: #e2e8f0;
          }
          
          .day-name {
            padding: 1rem;
            text-align: center;
            background-color: #061C5B;
            color: white;
            font-weight: 600;
            font-size: 0.875rem;
            transition: all 0.3s;
          }
          
          .day {
            padding: 0.75rem;
            text-align: center;
            background-color: white;
            min-height: 4rem;
            position: relative;
            border: 1px solid #e2e8f0;
            transition: all 0.3s;
          }
          
          .day:hover {
            background-color: #f7fafc;
            transform: scale(1.02);
            z-index: 1;
            animation: glow 2s infinite;
          }
          
          .day.empty {
            background-color: #f7fafc;
          }
          
          .event-day {
            background-color: rgba(6, 28, 91, 0.05);
            position: relative;
            animation: pulse 2s infinite;
          }
          
          .event-day::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background-color: #2b6cb0;
            animation: slideIn 0.5s ease-out forwards;
          }
          
          .event-details {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 0.25rem;
            animation: fadeIn 0.5s ease-out forwards;
          }
          
          .event-title {
            font-size: 0.75rem;
            color: #061C5B;
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            background-color: rgba(6, 28, 91, 0.05);
            padding: 0.25rem;
            border-radius: 0.25rem;
            transition: all 0.3s;
          }
          
          .event-title:hover {
            transform: scale(1.1);
            background-color: rgba(6, 28, 91, 0.1);
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            animation: fadeIn 0.3s ease-out forwards;
            backdrop-filter: blur(5px);
          }

          .modal-content {
            background-color: white;
            border-radius: 1rem;
            max-width: 600px;
            width: 90%;
            position: relative;
            animation: slideIn 0.3s ease-out forwards;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            background: linear-gradient(135deg, #061C5B 0%, #0A2B7C 100%);
            background-size: 200% 200%;
            animation: shine 8s ease infinite;
            color: white;
            padding: 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .modal-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin: 0;
            color: white;
          }

          .modal-close {
            background: none;
            border: none;
            font-size: 2rem;
            cursor: pointer;
            color: white;
            transition: all 0.3s;
            padding: 0;
            line-height: 1;
          }

          .modal-close:hover {
            transform: scale(1.2);
            color: #ebf8ff;
          }

          .modal-body {
            padding: 2rem;
          }

          .modal-info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
            margin-bottom: 2rem;
          }

          .modal-info-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
            background-color: white;
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            transition: all 0.3s;
          }

          .modal-info-item:hover {
            transform: translateY(-5px) scale(1.02);
            animation: glow 2s infinite;
          }

          .modal-info-icon {
            font-size: 1.5rem;
            background-color: rgba(6, 28, 91, 0.05);
            padding: 0.75rem;
            border-radius: 50%;
            color: #061C5B;
          }

          .modal-info-label {
            font-size: 0.75rem;
            color: #4a5568;
            margin: 0;
          }

          .modal-info-value {
            font-size: 1rem;
            color: #2d3748;
            font-weight: 600;
            margin: 0;
          }

          .modal-description {
            background-color: #f7fafc;
            padding: 1.5rem;
            border-radius: 0.5rem;
          }

          .modal-description-title {
            font-size: 1.25rem;
            color: #2d3748;
            margin-bottom: 1rem;
            font-weight: 600;
          }

          .modal-description-content {
            color: #4a5568;
            line-height: 1.6;
          }

          .modal-description-content p {
            margin-bottom: 1rem;
          }

          .modal-description-content h5 {
            color: #2d3748;
            margin-bottom: 0.5rem;
          }

          @media (max-width: 640px) {
            .calendar-container {
              grid-template-columns: 1fr;
              gap: 0.5rem;
            }
            
            .calendar {
              width: 100%;
              padding: 0.5rem;
            }
            
            .calendar-header {
              padding: 0.5rem;
            }
            
            .day {
              min-height: 2.5rem;
              padding: 0.25rem;
              font-size: 0.75rem;
            }
            
            .day-name {
              padding: 0.25rem;
              font-size: 0.7rem;
            }
            
            .event-title {
              font-size: 0.6rem;
              padding: 0.1rem;
              margin: 0.1rem;
            }
            
            .modal-content {
              width: 95%;
              margin: 0.5rem;
              max-height: 90vh;
              overflow-y: auto;
            }
            
            .modal-info-grid {
              grid-template-columns: 1fr;
              gap: 0.5rem;
            }
            
            .modal-info-item {
              padding: 0.5rem;
            }
            
            .proximo-evento {
              margin-top: 0.5rem;
              padding: 0.5rem;
            }
            
            .proximo-evento-card {
              padding: 0.5rem;
            }
            
            .proximo-evento-fecha {
              padding: 0.5rem;
            }
          }

          .calendar-container {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 2rem;
            align-items: start;
          }

          .proximo-evento {
            background: white;
            border-radius: 1rem;
            padding: 1.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            animation: float 6s ease-in-out infinite;
            border: 1px solid #e2e8f0;
          }

          .proximo-evento-titulo {
            font-size: 1.25rem;
            font-weight: 600;
            color: #061C5B;
            margin-bottom: 1.5rem;
            text-align: center;
            position: relative;
          }

          .proximo-evento-titulo::after {
            content: '';
            position: absolute;
            bottom: -0.5rem;
            left: 50%;
            transform: translateX(-50%);
            width: 50px;
            height: 3px;
            background-color: #061C5B;
            border-radius: 3px;
          }

          .proximo-evento-card {
            background: white;
            border-radius: 0.75rem;
            padding: 1.5rem;
            color: #061C5B;
            transition: all 0.3s;
            border: 1px solid #e2e8f0;
          }

          .proximo-evento-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          }

          .proximo-evento-fecha {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 1.5rem;
            background-color: rgba(6, 28, 91, 0.05);
            padding: 1rem;
            border-radius: 0.5rem;
          }

          .proximo-evento-dia {
            font-size: 2.5rem;
            font-weight: 700;
            line-height: 1;
            color: #061C5B;
          }

          .proximo-evento-mes {
            font-size: 1.25rem;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #061C5B;
          }

          .proximo-evento-info {
            text-align: center;
          }

          .proximo-evento-nombre {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: #061C5B;
          }

          .proximo-evento-hora {
            font-size: 1rem;
            color: #4a5568;
            margin-bottom: 1rem;
          }

          .proximo-evento-ubicacion {
            font-size: 0.875rem;
            color: #4a5568;
            margin-bottom: 1.5rem;
          }

          .proximo-evento-boton {
            background-color: #061C5B;
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 0.5rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s;
            width: 100%;
          }

          .proximo-evento-boton:hover {
            background-color: #0A2B7C;
            transform: translateY(-2px);
          }

          .proximo-evento-vacio {
            text-align: center;
            padding: 2rem;
            color: #4a5568;
            font-size: 1.1rem;
            opacity: 0.8;
            animation: fadeIn 0.5s ease-out forwards;
          }

          .proximo-evento-vacio p {
            margin: 0;
            font-style: italic;
          }

          @media (max-width: 1024px) {
            .calendar-container {
              grid-template-columns: 1fr;
            }
            
            .proximo-evento {
              animation: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Calendario; 